// aspectStore.ts — which frame the game preview pane wears, shared between the Header (which holds
// the Phone/iPad toggle, so nothing sits over the game itself) and GameView (which resizes to it).
// Same observable-singleton shape as targetStore.
//
// "phone" is the 18:39 the game was tuned against; "ipad" is classic iPad portrait (3:4) — the
// widest portrait the game ships to, whose layout bugs used to need a device to see. Switching is a
// pure CSS resize: Unity's loader re-matches its render target to the canvas size every frame, so
// no reload is involved.
//
// localStorage, not sessionStorage: which frame you preview in is a personal preference, not a
// per-tab safety choice like the write target.
export const ASPECTS = {
    phone: { ratio: "18 / 39", label: "Phone" },
    ipad: { ratio: "3 / 4", label: "iPad" },
};
const STORAGE_KEY = "conveyor.contentEditor.gameAspect";
class AspectStore {
    constructor() {
        // A temporary frame that wins over the user's choice without replacing it — level images are
        // rendered in the phone frame whatever the toggle says (see levelImages.ts). Not persisted.
        this.held = null;
        this.listeners = new Set();
        /** Shows `aspect` until the returned release is called; the user's own choice is untouched. */
        this.hold = (aspect) => {
            this.held = aspect;
            this.listeners.forEach((fn) => fn());
            return () => {
                this.held = null;
                this.listeners.forEach((fn) => fn());
            };
        };
        this.select = (aspect) => {
            if (this.held !== null) {
                return; // a render is holding the frame; switching mid-render would change the pictures
            }
            this.aspect = aspect;
            try {
                window.localStorage.setItem(STORAGE_KEY, aspect);
            }
            catch {
                // storage unavailable — the in-memory choice still applies for this page view
            }
            this.listeners.forEach((fn) => fn());
        };
        this.subscribe = (fn) => {
            this.listeners.add(fn);
            return () => this.listeners.delete(fn);
        };
        let stored = null;
        try {
            stored = window.localStorage.getItem(STORAGE_KEY);
        }
        catch {
            // storage blocked (private mode) — the toggle still works, it just forgets
        }
        this.aspect = stored && stored in ASPECTS ? stored : "phone";
    }
    get id() {
        return this.held ?? this.aspect;
    }
    get ratio() {
        return ASPECTS[this.id].ratio;
    }
    /** True while a temporary frame is held; the toggle shows it but cannot change it. */
    get isHeld() {
        return this.held !== null;
    }
}
export const aspectStore = new AspectStore();
